import React from "react";
import { Loader2 } from "lucide-react";
import CustomModal from "../../common/CustomModal";

interface FormModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  submitting?: boolean;
  submitDisabled?: boolean;
  width?: string;
  children: React.ReactNode;
}

export const FormModal: React.FC<FormModalProps> = ({
  open,
  title,
  onClose,
  onSubmit,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  submitting,
  submitDisabled,
  width = "w-full max-w-lg",
  children,
}) => (
  <CustomModal
    show={open}
    onHide={onClose}
    title={title}
    width={width}
    footer={
      <>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="rounded-md border border-cardborder bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {cancelLabel}
        </button>
        <button
          type="submit"
          form="rbac-form-modal-form"
          disabled={submitting || submitDisabled}
          className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand/90 disabled:opacity-50"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitLabel}
        </button>
      </>
    }
  >
    <form
      id="rbac-form-modal-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!submitDisabled && !submitting) onSubmit();
      }}
      className="w-full space-y-4 px-1 pb-2"
    >
      {children}
    </form>
  </CustomModal>
);

interface LabeledFieldProps {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}

export const LabeledField: React.FC<LabeledFieldProps> = ({
  label,
  required,
  hint,
  error,
  children,
}) => (
  <label className="block text-sm">
    <span className="mb-1 inline-flex items-center gap-1 font-medium text-gray-700">
      {label}
      {required && <span className="text-red-500">*</span>}
    </span>
    {children}
    {hint && !error && <span className="mt-1 block text-xs text-gray-500">{hint}</span>}
    {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
  </label>
);

interface BaseInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const TextInput: React.FC<BaseInputProps> = ({ invalid, className, ...rest }) => (
  <input
    {...rest}
    className={`block w-full rounded-md border bg-white px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-1 ${
      invalid
        ? "border-red-300 focus:border-red-400 focus:ring-red-400"
        : "border-cardborder focus:border-brand focus:ring-brand"
    } ${className ?? ""}`}
  />
);

interface SelectFieldProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const SelectInput: React.FC<SelectFieldProps> = ({ invalid, className, ...rest }) => (
  <select
    {...rest}
    className={`block w-full rounded-md border bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-1 ${
      invalid
        ? "border-red-300 focus:border-red-400 focus:ring-red-400"
        : "border-cardborder focus:border-brand focus:ring-brand"
    } ${className ?? ""}`}
  />
);
